import { createBrowserRouter } from 'react-router';
import { Layout } from './components/Layout';
import { AuthGate } from './components/AuthGate';
import { AppLoading, AppLoadError } from './components/AppLoading';
import { RootShell } from './components/Ga4Loader';

// Each route loads its page lazily so leaf-page code isn't shipped in the
// initial bundle. React Router calls `lazy()` the first time it navigates
// to the route, then caches the resolved Component.
const lazyPage = <K extends string>(loader: () => Promise<Record<K, React.ComponentType<unknown>>>, exportName: K) =>
  async () => {
    const mod = await loader();
    return { Component: mod[exportName] };
  };

// Lazily create router so popstate-guard can patch window.addEventListener first
let _router: ReturnType<typeof createBrowserRouter> | null = null;
export function getRouter() {
  if (!_router) {
    _router = createBrowserRouter([
      {
        // Pathless shell around every route. HydrateFallback shows the
        // branded loading screen while the first route's chunk downloads
        // (the router renders nothing during that window otherwise), and
        // ErrorBoundary catches route errors — including chunk downloads
        // that 404 because the tab predates the latest deploy — with a
        // reload prompt instead of a dead end.
        Component: RootShell,
        HydrateFallback: AppLoading,
        ErrorBoundary: AppLoadError,
        children: [
          // Public routes — accessible without auth
          {
            // Marketing homepage (CMS-driven, see data/marketing.ts).
            // Signed-out visitors to the site root also land on this page
            // via AuthGate; signed-in users keep the Dashboard at /.
            path: '/home',
            lazy: lazyPage(() => import('./components/marketing/MarketingSitePage'), 'MarketingHomePage'),
          },
          {
            // Additional CMS marketing pages, e.g. /p/features.
            path: '/p/:slug',
            lazy: lazyPage(() => import('./components/marketing/MarketingSitePage'), 'MarketingSitePage'),
          },
          {
            // Stable sign-in URL for marketing-page links; bounces
            // already-signed-in visitors into the app.
            path: '/login',
            lazy: lazyPage(() => import('./components/LoginRoute'), 'LoginRoute'),
          },
          {
            path: '/board',
            lazy: lazyPage(() => import('./components/PublicBoard'), 'PublicBoard'),
          },
          {
            path: '/board/submit-content',
            lazy: lazyPage(() => import('./components/SocialMediaForm'), 'SocialMediaForm'),
          },
          {
            path: '/checkin',
            lazy: lazyPage(() => import('./components/CheckInKiosk'), 'CheckInKiosk'),
          },
          {
            // TV Mode: the company-wide wall dashboard. Outside AuthGate on
            // purpose — a wall TV, or a StageCommand Display, has no one to
            // sign in, and signing in (Google sends the browser back to '/')
            // would lose the page anyway. It shows the whole organization's
            // open deliverables, not one person's.
            path: '/tv',
            lazy: lazyPage(() => import('./components/TVDashboard'), 'TVDashboard'),
          },
          {
            // OAuth return URL — must stay outside AuthGate so the completing-
            // sign-in spinner shows while Supabase consumes the token fragment.
            path: '/auth/callback',
            lazy: lazyPage(() => import('./components/AuthCallback'), 'AuthCallback'),
          },
          // Protected routes — wrapped in AuthGate
          {
            Component: AuthGate,
            children: [
              {
                path: '/',
                Component: Layout,
                children: [
                  { index: true, lazy: lazyPage(() => import('./components/Dashboard'), 'Dashboard') },
                  { path: 'projects', lazy: lazyPage(() => import('./components/ProjectsPage'), 'ProjectsPage') },
                  { path: 'projects/:id', lazy: lazyPage(() => import('./components/ProjectDetailPage'), 'ProjectDetailPage') },
                  { path: 'calendar', lazy: lazyPage(() => import('./components/CalendarPage'), 'CalendarPage') },
                  { path: 'meetings/:eventId', lazy: lazyPage(() => import('./components/agenda/MeetingAgendaPage'), 'MeetingAgendaPage') },
                  { path: 'venues', lazy: lazyPage(() => import('./components/VenuesPage'), 'VenuesPage') },
                  { path: 'content', lazy: lazyPage(() => import('./components/ContentPage'), 'ContentPage') },
                  { path: 'volunteers', lazy: lazyPage(() => import('./components/VolunteersPage'), 'VolunteersPage') },
                  { path: 'merch', lazy: lazyPage(() => import('./components/MerchPage'), 'MerchPage') },
                  { path: 'team', lazy: lazyPage(() => import('./components/TeamPage'), 'TeamPage') },
                  // Employee hub. These pages load their own data with the
                  // user's token rather than through DataContext — HR records
                  // must not ride the /api/all payload every browser receives.
                  { path: 'hr', lazy: lazyPage(() => import('./components/hr/HrHubPage'), 'HrHubPage') },
                  { path: 'hr/directory', lazy: lazyPage(() => import('./components/hr/HrDirectoryPage'), 'HrDirectoryPage') },
                  { path: 'hr/org-chart', lazy: lazyPage(() => import('./components/hr/HrOrgChartPage'), 'HrOrgChartPage') },
                  { path: 'hr/people/:teamMemberId', lazy: lazyPage(() => import('./components/hr/HrPersonPage'), 'HrPersonPage') },
                  { path: 'hr/handbook', lazy: lazyPage(() => import('./components/hr/HrHandbookPage'), 'HrHandbookPage') },
                  { path: 'hr/handbook/:slug', lazy: lazyPage(() => import('./components/hr/HrPolicyPage'), 'HrPolicyPage') },
                  { path: 'hr/admin/policies', lazy: lazyPage(() => import('./components/hr/HrPolicyAdminPage'), 'HrPolicyAdminPage') },
                  { path: 'hr/time-off', lazy: lazyPage(() => import('./components/hr/HrTimeOffPage'), 'HrTimeOffPage') },
                  { path: 'hr/time-off/approvals', lazy: lazyPage(() => import('./components/hr/HrTimeOffApprovalsPage'), 'HrTimeOffApprovalsPage') },
                  { path: 'hr/workflows/:instanceId', lazy: lazyPage(() => import('./components/hr/HrWorkflowPage'), 'HrWorkflowPage') },
                  { path: 'hr/admin/workflows', lazy: lazyPage(() => import('./components/hr/HrWorkflowAdminPage'), 'HrWorkflowAdminPage') },
                  { path: 'hr/certifications', lazy: lazyPage(() => import('./components/hr/HrCertificationsPage'), 'HrCertificationsPage') },
                  { path: 'hr/admin/certifications', lazy: lazyPage(() => import('./components/hr/HrCertificationAdminPage'), 'HrCertificationAdminPage') },
                  { path: 'hr/performance', lazy: lazyPage(() => import('./components/hr/HrPerformancePage'), 'HrPerformancePage') },
                  { path: 'hr/reviews/:reviewId', lazy: lazyPage(() => import('./components/hr/HrPerformancePage'), 'HrReviewPage') },
                  { path: 'hr/admin/review-cycles', lazy: lazyPage(() => import('./components/hr/HrReviewCycleAdminPage'), 'HrReviewCycleAdminPage') },
                  { path: 'hr/admin/roles', lazy: lazyPage(() => import('./components/hr/HrRoleAdminPage'), 'HrRoleAdminPage') },
                  { path: 'hr/admin/payroll', lazy: lazyPage(() => import('./components/hr/HrPayrollPage'), 'HrPayrollPage') },
                  { path: 'team/presence', lazy: lazyPage(() => import('./components/PresencePage'), 'PresencePage') },
                  { path: 'team/hours', lazy: lazyPage(() => import('./components/HoursPage'), 'HoursPage') },
                  { path: 'new-deliverable', lazy: lazyPage(() => import('./components/DeliverableForm'), 'DeliverableForm') },
                  { path: 'deliverables', lazy: lazyPage(() => import('./components/DeliverablesPage'), 'DeliverablesPage') },
                  { path: 'deliverables/plan', lazy: lazyPage(() => import('./components/deliverables/PlanDeliverablePage'), 'PlanDeliverablePage') },
                  { path: 'deliverables/:id', lazy: lazyPage(() => import('./components/DeliverableDetailPage'), 'DeliverableDetailPage') },
                  { path: 'volunteer-signup/:opportunityId', lazy: lazyPage(() => import('./components/VolunteerSignupForm'), 'VolunteerSignupForm') },
                  { path: 'integrations', lazy: lazyPage(() => import('./components/IntegrationsPage'), 'IntegrationsPage') },
                  { path: 'inbox', lazy: lazyPage(() => import('./components/AdminInboxPage'), 'AdminInboxPage') },
                  { path: 'community/form-builder', lazy: lazyPage(() => import('./components/FormBuilderPage'), 'FormBuilderPage') },
                  { path: 'community/moderation', lazy: lazyPage(() => import('./components/ModerationSettingsPage'), 'ModerationSettingsPage') },
                  { path: 'community/polls', lazy: lazyPage(() => import('./components/PollsPage'), 'PollsPage') },
                  { path: 'super-admin', lazy: lazyPage(() => import('./components/SuperAdminPage'), 'SuperAdminPage') },
                  { path: 'settings/api', lazy: lazyPage(() => import('./components/ApiSettingsPage'), 'ApiSettingsPage') },
                  { path: 'settings/form-options', lazy: lazyPage(() => import('./components/DeptFormOptionsPage'), 'DeptFormOptionsPage') },
                  { path: 'settings/departments', lazy: lazyPage(() => import('./components/DepartmentsAdminPage'), 'DepartmentsAdminPage') },
                  { path: 'settings/locations', lazy: lazyPage(() => import('./components/LocationsAdminPage'), 'LocationsAdminPage') },
                  { path: 'settings/audit-log', lazy: lazyPage(() => import('./components/AuditLogPage'), 'AuditLogPage') },
                  { path: 'settings/marketing', lazy: lazyPage(() => import('./components/marketing/MarketingAdminPage'), 'MarketingAdminPage') },
                  { path: 'templates', lazy: lazyPage(() => import('./components/TemplatesPage'), 'TemplatesPage') },
                  { path: 'digest', lazy: lazyPage(() => import('./components/DigestPage'), 'DigestPage') },
                  { path: 'help', lazy: lazyPage(() => import('./components/help/HelpPage'), 'HelpPage') },
                  { path: 'help/:slug', lazy: lazyPage(() => import('./components/help/HelpArticlePage'), 'HelpArticlePage') },
                  { path: '*', lazy: lazyPage(() => import('./components/NotFound'), 'NotFound') },
                ],
              },
              {
                path: '/focus',
                lazy: lazyPage(() => import('./components/FocusMode'), 'FocusMode'),
              },
              {
                path: '/focus/mobile',
                lazy: lazyPage(() => import('./components/MobileFocus'), 'MobileFocus'),
              },
            ],
          },
          {
            path: '*',
            lazy: lazyPage(() => import('./components/NotFound'), 'NotFound'),
          },
        ],
      },
    ]);
  }
  return _router;
}
